import * as _ from 'lodash';

import { SeasonName } from 'helpers';
import {
    isProductSearchResult,
    ProductSearchResponse,
    ProductSearchResult
} from 'services/product-search/searcher.types';

export interface ContentGroupedProduct {
    styleGroupId: string;
    colorGroupId: string;
    variants: ContentGroupedProductVariant[];
    oldestCreatedDate?: string;
}

interface ContentGroupedProductVariant {
    articleNumber?: string;
    hasImages?: boolean;
    fcKeys?: {
        seasonKey?: string;
        seasonYearKey?: string;
        categoryKey?: string;
        detailedCategoryKey?: {
            top?: string;
            main?: string;
            level1?: string[];
        };
        detailedCategory?: {
            top?: string;
            main?: string;
            level1?: string[];
        };
    };
    media?: {
        images?: unknown[];
    };
}

interface ContentProductSortKeys {
    seasonWeight: number;
    mainCategory: string;
    level1: string;
    hasImages: boolean;
    articleNumber: string;
}

const SEASON_NAME_SORT_SUFFIX = {
    [SeasonName.SpringSummer]: '100',
    [SeasonName.FallWinter]: '500'
};

function getContentProductSortKeys(product: ContentGroupedProduct): ContentProductSortKeys {
    const variant = _.first(product.variants);
    const fcKeys = variant?.fcKeys;
    const detailedCategory = fcKeys?.detailedCategoryKey ?? fcKeys?.detailedCategory;
    const { seasonKey, seasonYearKey } = fcKeys ?? {};

    let seasonWeight = 0;
    if (seasonKey === SeasonName.NOS) {
        seasonWeight = 1_000_000;
    } else if (seasonKey && seasonYearKey) {
        seasonWeight = parseInt(`${seasonYearKey}${_.get(SEASON_NAME_SORT_SUFFIX, seasonKey, '000')}`, 10);
    }

    const level1Values = detailedCategory?.level1;

    return {
        seasonWeight,
        mainCategory: detailedCategory?.main || fcKeys?.categoryKey || detailedCategory?.top || '',
        level1: _.isEmpty(level1Values) ? '' : (_.first(_.sortBy(level1Values)) ?? ''),
        hasImages: variant?.hasImages === true || !_.isEmpty(variant?.media?.images),
        articleNumber: variant?.articleNumber ?? ''
    };
}

function compareContentProducts(first: ContentGroupedProduct, second: ContentGroupedProduct): number {
    const firstKeys = getContentProductSortKeys(first);
    const secondKeys = getContentProductSortKeys(second);

    return (
        secondKeys.seasonWeight - firstKeys.seasonWeight ||
        firstKeys.mainCategory.localeCompare(secondKeys.mainCategory) ||
        firstKeys.level1.localeCompare(secondKeys.level1) ||
        Number(secondKeys.hasImages) - Number(firstKeys.hasImages) ||
        secondKeys.articleNumber.localeCompare(firstKeys.articleNumber, undefined, { numeric: true })
    );
}

export function sortContentProducts<T extends ContentGroupedProduct>(products: T[]): T[] {
    return [...products].sort(compareContentProducts);
}

export function sortContentProductSearchResult(result: ProductSearchResponse): ProductSearchResponse {
    if (!isProductSearchResult(result)) {
        return result;
    }

    return {
        ...result,
        products: sortContentProducts(result.products as ContentGroupedProduct[])
    } as ProductSearchResult;
}
